import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { ErrorState, LoadingState } from '@/components/common/States';
import { paymentsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import type { Receipt } from '@/types/finance';
import { formatDate, formatMoney, orDash } from '@/utils/format';

/**
 * A receipt, laid out for paper.
 *
 * <p>Printed through the browser rather than generated as a PDF server-side.
 * That is a deliberate trade: a print stylesheet gives the same document, in the
 * user's own paper size, with no new dependency and no second layout engine to
 * keep in step with this one. Print to PDF is a first-class browser feature and
 * the resulting file is identical.
 *
 * <p>Every figure comes from the payment as it stands right now. Nothing is
 * cached and no receipt is stored, so a copy printed after a refund shows the
 * refund — which is the whole reason receipts are rendered rather than filed.
 */
export function ReceiptPage() {
  const { id = '' } = useParams();

  const query = useQuery({
    queryKey: queryKeys.payments.receipt(id),
    queryFn: () => paymentsApi.receipt(id),
    enabled: id !== '',
  });

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return <LoadingState label="Preparing the receipt" />;
  }

  return (
    <div>
      {/* Everything in this block is screen-only. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to={ROUTES.paymentDetail(id)}
              className="inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to the payment
        </Link>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print or save as PDF
        </Button>
      </div>

      <ReceiptDocument receipt={query.data} />
    </div>
  );
}

/**
 * The printable receipt itself. Shared with the student and parent portal, so a
 * family's copy is the office's copy.
 */
export function ReceiptDocument({ receipt }: { receipt: Receipt }) {
  const currency = receipt.currencyCode;

  return (
    <>
      {receipt.invalidationNotice !== undefined && (
        <p className="mb-4 border-2 border-danger bg-danger-soft px-4 py-3 text-[13px] font-semibold text-danger">
          {receipt.invalidationNotice}
        </p>
      )}

      <article className="receipt mx-auto max-w-[760px] border-2 border-line bg-surface px-4 py-6 sm:px-8 sm:py-8 print:max-w-none print:border-0 print:px-0 print:py-0">
        <header className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-line pb-5">
          <div>
            <h1 className="text-[22px] font-extrabold leading-tight text-ink">
              {receipt.school?.name ?? 'School'}
            </h1>
            <p className="mt-1 text-[12px] leading-relaxed text-muted">
              {[receipt.school?.addressLine1, receipt.school?.addressLine2, receipt.school?.city]
                .filter((line) => line !== undefined && line !== '')
                .join(', ')}
              {receipt.school?.phone !== undefined && (
                <span className="block">{receipt.school.phone}</span>
              )}
              {receipt.school?.email !== undefined && (
                <span className="block">{receipt.school.email}</span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
              Payment receipt
            </p>
            <p className="mt-1 font-mono text-[17px] font-bold text-ink">{receipt.receiptNumber}</p>
            <p className="mt-1 text-[12px] text-muted">{formatDate(receipt.paymentDate)}</p>
          </div>
        </header>

        <section className="grid gap-6 border-b border-line py-5 sm:grid-cols-2">
          <Block title="Received from">
            <p className="text-[14px] font-semibold text-ink">
              {receipt.payer?.name ?? orDash(receipt.student.name)}
            </p>
            {receipt.payer?.phone !== undefined && (
              <p className="text-[12px] text-muted">{receipt.payer.phone}</p>
            )}
            {receipt.payer?.email !== undefined && (
              <p className="text-[12px] text-muted">{receipt.payer.email}</p>
            )}
          </Block>
          <Block title="On behalf of">
            <p className="text-[14px] font-semibold text-ink">{orDash(receipt.student.name)}</p>
            <p className="text-[12px] text-muted">
              {orDash(receipt.student.studentNumber)}
              {receipt.student.gradeName !== undefined && ` · ${receipt.student.gradeName}`}
              {receipt.student.sectionName !== undefined && ` ${receipt.student.sectionName}`}
            </p>
          </Block>
        </section>

        {receipt.settled.length > 0 && (
          <section className="border-b border-line py-5">
            <h2 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
              Applied to
            </h2>
            <div className="overflow-x-auto print:overflow-visible">
              <table className="w-full min-w-[480px] text-[13px] print:min-w-0">
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className="pb-1.5 text-left font-semibold text-muted">Invoice</th>
                    <th scope="col" className="pb-1.5 text-left font-semibold text-muted">Period</th>
                    <th scope="col" className="pb-1.5 text-right font-semibold text-muted">Bill</th>
                    <th scope="col" className="pb-1.5 text-right font-semibold text-muted">Applied</th>
                    <th scope="col" className="pb-1.5 text-right font-semibold text-muted">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.settled.map((line) => (
                    <tr key={line.invoiceNumber} className="border-b border-line/60 last:border-0">
                      <td className="py-1.5 font-mono text-[12px] text-ink">{line.invoiceNumber}</td>
                      <td className="py-1.5 text-muted">{orDash(line.billingPeriod)}</td>
                      <td className="py-1.5 text-right tabular-nums text-muted">
                        {formatMoney(line.invoiceTotal, currency)}
                      </td>
                      <td className="py-1.5 text-right font-semibold tabular-nums text-ink">
                        {formatMoney(line.amountApplied, currency)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-muted">
                        {formatMoney(line.invoiceOutstanding, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="flex flex-wrap items-end justify-between gap-6 py-5">
          <div className="max-w-[380px]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
              Amount in words
            </p>
            <p className="mt-1 text-[13px] italic leading-relaxed text-ink">
              {receipt.amountInWords}
            </p>
            <dl className="mt-3 space-y-0.5 text-[12px] text-muted">
              <Pair label="Method" value={receipt.paymentMethod.replaceAll('_', ' ').toLowerCase()} />
              {receipt.referenceNumber !== undefined && (
                <Pair label="Reference" value={receipt.referenceNumber} />
              )}
              {receipt.bankName !== undefined && <Pair label="Bank" value={receipt.bankName} />}
            </dl>
          </div>

          <div className="text-right sm:min-w-[230px]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
              Amount received
            </p>
            <p className="mt-0.5 text-[30px] font-extrabold leading-none tabular-nums text-ink">
              {formatMoney(receipt.amountPaid, currency)}
            </p>
            {receipt.heldOnAccount > 0 && (
              <p className="mt-2 text-[12px] text-muted">
                Held on account {formatMoney(receipt.heldOnAccount, currency)}
              </p>
            )}
            <p className="mt-1 text-[12px] text-muted">
              Balance remaining {formatMoney(receipt.balanceRemaining, currency)}
            </p>
          </div>
        </section>

        <footer className="flex flex-wrap items-end justify-between gap-6 border-t-2 border-line pt-5">
          <p className="max-w-[380px] text-[11px] leading-relaxed text-subtle">
            {receipt.notes ?? 'This receipt confirms the amount received. Please retain it for your records.'}
          </p>
          <div className="text-right">
            <div className="h-9 w-[190px] max-w-full border-b border-line" />
            <p className="mt-1 text-[11px] text-subtle">
              {receipt.receivedByName ?? 'Received by'}
            </p>
          </div>
        </footer>
      </article>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-subtle">{label}:</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
